import { useState, useEffect } from "react";

import { sharedApi } from "../services/sharedApi";
import { logger } from "../utils/logger";

export type OAuthProvider = "google" | "microsoft" | "apple" | "facebook";

interface UseOAuthProvidersResult {
  enabledProviders: string[];
  loading: boolean;
  isProviderEnabled: (provider: string) => boolean;
  isProviderConfigured: (provider: string) => boolean;
}

/**
 * Hook to check which OAuth providers are enabled on the backend
 * and which have client-side configuration (env variables)
 */
export function useOAuthProviders(): UseOAuthProvidersResult {
  const [enabledProviders, setEnabledProviders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProviders = async (): Promise<void> => {
      try {
        const response = await sharedApi.auth.getOAuthProviders();
        const providers = response.providers.map((p) => p.toLowerCase());
        logger.debug(
          "[useOAuthProviders] Fetched providers from backend:",
          response.providers,
        );
        logger.debug("[useOAuthProviders] Normalized to lowercase:", providers);
        setEnabledProviders(providers);
      } catch (error) {
        logger.error("Failed to fetch OAuth providers:", error);
        // Don't enable all by default - respect backend configuration
        setEnabledProviders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, []);

  /**
   * Check if provider is enabled on backend
   * During loading, optimistically returns true if provider is configured
   * (prevents flickering of disabled state)
   */
  const isProviderEnabled = (provider: string): boolean => {
    if (loading) {
      logger.debug(
        "[useOAuthProviders] isProviderEnabled - still loading, checking configuration optimistically",
      );
      // Optimistic: if it's configured, assume it might be enabled
      // This prevents showing disabled buttons during initial load
      return isProviderConfigured(provider);
    }
    const result = enabledProviders.includes(provider.toLowerCase());
    logger.debug(`[useOAuthProviders] isProviderEnabled("${provider}"):`, {
      enabledProviders,
      result,
    });
    return result;
  };

  /**
   * Check if provider has client-side configuration (env variables)
   */
  const isProviderConfigured = (provider: string): boolean => {
    let result = false;
    switch (provider.toLowerCase()) {
      case "google":
        result = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;
        break;
      case "microsoft":
        result = !!import.meta.env.VITE_MICROSOFT_CLIENT_ID;
        break;
      case "apple":
        result = !!import.meta.env.VITE_APPLE_CLIENT_ID;
        break;
      case "facebook":
        result = !!import.meta.env.VITE_FACEBOOK_APP_ID;
        break;
      default:
        result = false;
    }
    logger.debug(
      `[useOAuthProviders] isProviderConfigured("${provider}"): ${result}`,
    );
    return result;
  };

  return {
    enabledProviders,
    loading,
    isProviderEnabled,
    isProviderConfigured,
  };
}

/**
 * Check if provider is fully available (backend enabled + client configured)
 */
export function isProviderAvailable(
  provider: string,
  enabledProviders: string[],
  isProviderConfigured: (provider: string) => boolean,
): boolean {
  const enabled = enabledProviders.includes(provider.toLowerCase());
  const configured = isProviderConfigured(provider);
  return enabled && configured;
}
